import { appUrl } from "@/constants";
import { BrandColors, Colors } from "@/constants/theme";
import { trackLogin } from "@/lib/analytics-events";
import type { AppRole } from "@/lib/roles";
import { showToast } from "@/lib/toast";
import { useAuthStore } from "@/stores/use-auth-store";
import FontAwesome from "@react-native-vector-icons/fontawesome";
import * as Linking from "expo-linking";
import * as WebBrowser from "expo-web-browser";
import { useState } from "react";
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { ThemedText } from "../themed-text";

WebBrowser.maybeCompleteAuthSession();

type SocialProvider = "google" | "facebook" | "apple";

const providers: {
  id: SocialProvider;
  label: string;
  icon: (textColor: string) => React.ReactNode;
  smallIcon: (textColor: string) => React.ReactNode;
  accessibilityLabel: string;
}[] = [
  {
    id: "google",
    label: "Google",
    icon: () => <FontAwesome name="google" size={22} color="#EA4335" />,
    smallIcon: () => <FontAwesome name="google" size={16} color="#EA4335" />,
    accessibilityLabel: "Continue with Google",
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: () => <FontAwesome name="facebook" size={24} color="#1877F2" />,
    smallIcon: () => <FontAwesome name="facebook-official" size={16} color="#1877F2" />,
    accessibilityLabel: "Continue with Facebook",
  },
  {
    id: "apple",
    label: "Apple",
    icon: (textColor) => <FontAwesome name="apple" size={24} color={textColor} />,
    smallIcon: (textColor) => <FontAwesome name="apple" size={17} color={textColor} />,
    accessibilityLabel: "Continue with Apple",
  },
];

const labeledProviderOrder: SocialProvider[] = ["google", "apple", "facebook"];

export function getOAuthRedirectUri(): string {
  if (Platform.OS === "web") {
    return Linking.createURL("callback");
  }

  return "autotransporter://callback";
}

export function parseOAuthCallbackUrl(callbackUrl: string): {
  token: string | null;
  user: string | null;
  error: string | null;
  provider: string | null;
} {
  const url = new URL(callbackUrl);
  const params = url.searchParams;

  // Deep links may put params in the query string after the path
  return {
    token: params.get("token"),
    user: params.get("user"),
    error: params.get("error"),
    provider: params.get("provider"),
  };
}

export function completeOAuthLogin(callbackUrl: string): {
  ok: boolean;
  provider?: string;
  error?: string;
} {
  const { token, user, error, provider } = parseOAuthCallbackUrl(callbackUrl);

  if (error) {
    return { ok: false, error: decodeURIComponent(error) };
  }

  if (!token || !user) {
    return {
      ok: false,
      error: "Failed to sign in with social login. Please try again.",
    };
  }

  try {
    const parsedUser = JSON.parse(atob(decodeURIComponent(user)));
    useAuthStore.getState().logIn({
      user: parsedUser,
      token: decodeURIComponent(token),
    });
    trackLogin(provider ?? "email" as any, parsedUser.role);

    return { ok: true, provider: provider ?? undefined };
  } catch {
    return {
      ok: false,
      error: "Failed to sign in with social login. Please try again.",
    };
  }
}

export function OAuth({
  role,
  variant = "icons",
  dividerLabel = "Or sign in with",
}: {
  role?: AppRole;
  variant?: "icons" | "labeled";
  dividerLabel?: string;
}) {
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const [socialLoading, setSocialLoading] = useState<SocialProvider | null>(
    null,
  );

  const handleSocialLogin = async (provider: SocialProvider) => {
    setSocialLoading(provider);
    try {
      const redirectUri = getOAuthRedirectUri();
      const authUrl =
        `${appUrl}/api/${provider}/redirect` +
        `?app_redirect=${encodeURIComponent(redirectUri)}` +
        (role ? `&role=${encodeURIComponent(role)}` : "");

      const result = await WebBrowser.openAuthSessionAsync(
        authUrl,
        redirectUri,
      );

      if (result.type !== "success") {
        showToast.error(
          "Failed to sign in with social login. Please try again.",
        );
        return;
      }

      const loginResult = completeOAuthLogin(result.url);
      if (!loginResult.ok) {
        showToast.error(
          loginResult.error ??
            "Failed to sign in with social login. Please try again.",
        );
      }
    } catch {
      showToast.error("Failed to open social login. Please try again.");
    } finally {
      setSocialLoading(null);
    }
  };

  if (variant === "labeled") {
    return (
      <>
        <View style={styles.labeledDivider}>
          <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
          <ThemedText style={styles.labeledDividerText}>{dividerLabel}</ThemedText>
          <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
        </View>
        <View style={styles.labeledContainer}>
          {labeledProviderOrder.map((providerId) => {
            const provider = providers.find((item) => item.id === providerId)!;

            return (
              <TouchableOpacity
                key={provider.id}
                style={[
                  styles.labeledButton,
                  { borderColor: theme.border, backgroundColor: theme.background },
                  socialLoading === provider.id && styles.iconButtonLoading,
                ]}
                onPress={() => handleSocialLogin(provider.id)}
                disabled={!!socialLoading}
                activeOpacity={0.75}
                accessibilityRole="button"
                accessibilityLabel={provider.accessibilityLabel}
              >
                {socialLoading === provider.id ? (
                  <ActivityIndicator size="small" color={BrandColors.primary} />
                ) : (
                  provider.smallIcon(theme.text)
                )}
                <ThemedText style={styles.labeledButtonText}>{provider.label}</ThemedText>
              </TouchableOpacity>
            );
          })}
        </View>
        <View style={styles.labeledDivider}>
          <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
          <ThemedText style={styles.labeledDividerText}>Or</ThemedText>
          <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
        </View>
      </>
    );
  }

  return (
    <>
      <View style={styles.container}>
        {providers.map((provider) => (
          <TouchableOpacity
            key={provider.id}
            style={[
              styles.iconButton,
              { borderColor: theme.border },
              socialLoading === provider.id && styles.iconButtonLoading,
            ]}
            onPress={() => handleSocialLogin(provider.id)}
            disabled={!!socialLoading}
            activeOpacity={0.75}
            accessibilityRole="button"
            accessibilityLabel={provider.accessibilityLabel}
          >
            {socialLoading === provider.id ? (
              <ActivityIndicator size="small" color={BrandColors.primary} />
            ) : (
              provider.icon(theme.text)
            )}
          </TouchableOpacity>
        ))}
      </View>
      <View style={styles.divider}>
        <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
        <ThemedText style={styles.dividerText}>Or</ThemedText>
        <View style={[styles.dividerLine, { backgroundColor: theme.border }]} />
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
    marginBottom: 24,
  },
  iconButton: {
    width: 56,
    height: 56,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    borderWidth: 1,
  },
  iconButtonLoading: {
    opacity: 0.7,
  },
  divider: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 24,
    gap: 12,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
  dividerText: {
    fontSize: 14,
    color: BrandColors.mediumGray,
  },
  labeledDivider: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginVertical: 16,
  },
  labeledDividerText: {
    fontSize: 12,
    lineHeight: 16,
    color: BrandColors.mediumGray,
  },
  labeledContainer: {
    flexDirection: "row",
    gap: 8,
  },
  labeledButton: {
    flex: 1,
    height: 40,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    borderRadius: 6,
    borderWidth: 1,
  },
  labeledButtonText: {
    fontSize: 14,
    lineHeight: 20,
  },
});
